import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  MovementApproval,
  MovementApprovalCreate,
  MovementApprovalUpdate
} from '../models/MovementApproval';
import { ErrorService } from '../shared/services/error.service';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';
import { PagedResult, PaginationOptions } from '../shared/interfaces/pagination.interface';

export type MovementApprovalPagedResult = PagedResult<MovementApproval>;

@Injectable({
  providedIn: 'root'
})
export class MovementApprovalsService {
  private get baseUrl() { return `${environment.microservices.stock}`; }

  constructor(
    private http: HttpClient,
    private errorService: ErrorService,
    private authService: AuthService
  ) {}

  // ===== CRUD OPERATIONS =====

  getAll(options?: PaginationOptions): Observable<MovementApprovalPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.baseUrl}/movement-approvals`, {
      params,
      headers: this.buildJwtHeaders()
    }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar aprovações de movimento');
        return throwError(() => err);
      })
    );
  }

  getById(id: number): Observable<MovementApproval> {
    return this.http.get<any>(`${this.baseUrl}/movement-approval/${id}`, {
      headers: this.buildJwtHeaders()
    }).pipe(
      map(dto => this.mapFromDto(dto)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar aprovação de movimento'); return throwError(() => err); })
    );
  }

  create(payload: MovementApprovalCreate): Observable<MovementApproval> {
    const dto = this.mapToDto(payload);
    return this.http.post<any>(`${this.baseUrl}/movement-approvals`, dto, {
      headers: this.buildJwtHeaders()
    }).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao criar aprovação de movimento'); return throwError(() => err); })
    );
  }

  update(id: number, payload: MovementApprovalUpdate): Observable<MovementApproval> {
    const dto = this.mapToDto(payload);
    return this.http.put<any>(`${this.baseUrl}/movement-approvals/${id}`, dto, {
      headers: this.buildJwtHeaders()
    }).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao atualizar aprovação de movimento'); return throwError(() => err); })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/movement-approvals/${id}`, {
      headers: this.buildJwtHeaders()
    }).pipe(
      catchError(err => { this.errorService.showErrorMessage('Erro ao excluir aprovação de movimento'); return throwError(() => err); })
    );
  }

  // ===== SPECIFIC QUERIES =====

  getPending(options?: PaginationOptions): Observable<MovementApprovalPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.baseUrl}/pending-approvals`, {
      params,
      headers: this.buildJwtHeaders()
    }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar aprovações pendentes'); return throwError(() => err); })
    );
  }

  getByApprover(approverId: number, options?: PaginationOptions): Observable<MovementApprovalPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.baseUrl}/movement-approvals/approver/${approverId}`, {
      params,
      headers: this.buildJwtHeaders()
    }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showErrorMessage('Erro ao buscar aprovações por aprovador'); return throwError(() => err); })
    );
  }

  private buildJwtHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    return token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : new HttpHeaders();
  }

  // ===== MAPPING METHODS =====

  private mapFromDto(dto: any): MovementApproval {
    const pick = (o: any, keys: string[]) => keys.map(k => o?.[k]).find(v => v !== undefined);
    const toIso = (d: any) => (d ? new Date(d).toISOString() : undefined);

    return {
      id: pick(dto, ['Id', 'id']) ?? 0,
      stockMovementId: pick(dto, ['StockMovementId', 'stockMovementId']) ?? 0,
      approverId: pick(dto, ['ApproverId', 'approverId']) ?? 0,
      approvalDate: toIso(pick(dto, ['ApprovalDate', 'approvalDate'])) ?? new Date().toISOString(),
      status: pick(dto, ['Status', 'status']) ?? 'Pending',
      justification: pick(dto, ['Justification', 'justification']) ?? '',
      reviewNotes: pick(dto, ['ReviewNotes', 'reviewNotes']),
      createdAt: toIso(pick(dto, ['CreatedAt', 'createdAt'])),
      updatedAt: toIso(pick(dto, ['UpdatedAt', 'updatedAt'])),
      createdBy: pick(dto, ['CreatedBy', 'createdBy']),
      updatedBy: pick(dto, ['UpdatedBy', 'updatedBy']),
      stockMovementQuantity: pick(dto, ['stockMovementQuantity', 'StockMovementQuantity']),
      stockMovementDate: toIso(pick(dto, ['stockMovementDate', 'StockMovementDate'])),
      solicitanteNome: pick(dto, ['solicitanteNome', 'SolicitanteNome']),
      aprovadorNome: pick(dto, ['aprovadorNome', 'AprovadorNome']),
      approvedBy: pick(dto, ['approvedBy', 'ApprovedBy']),
      approvedAt: toIso(pick(dto, ['approvedAt', 'ApprovedAt'])),

      // Dados relacionados
      stockMovement: pick(dto, ['StockMovement', 'stockMovement']),
      approverName: pick(dto, ['ApproverName', 'approverName']) ?? pick(dto, ['aprovadorNome', 'AprovadorNome']),
      approverDepartment: pick(dto, ['ApproverDepartment', 'approverDepartment'])
    };
  }

  private mapListFromDto(list: any[]): MovementApproval[] {
    if (!list || !Array.isArray(list)) return [];
    return list.map(x => this.mapFromDto(x));
  }

  private mapToDto(payload: Partial<MovementApprovalCreate | MovementApprovalUpdate>): any {
    return {
      StockMovementId: payload.stockMovementId,
      ApproverId: payload.approverId,
      ApprovalDate: payload.approvalDate ? new Date(payload.approvalDate).toISOString() : null,
      Status: payload.status,
      Justification: payload.justification,
      ReviewNotes: payload.reviewNotes
    };
  }

  private mapPagedResponse(response: any): MovementApprovalPagedResult {
    const items = Array.isArray(response?.Items) ? response.Items : Array.isArray(response?.items) ? response.items : [];
    const page = response?.Page ?? response?.page ?? 1;
    const pageSize = response?.PageSize ?? response?.pageSize ?? items.length;
    const totalItems = response?.TotalItems ?? response?.totalItems ?? response?.TotalRecords ?? response?.totalRecords ?? items.length;
    const totalPages = response?.TotalPages ?? response?.totalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);

    return { items: this.mapListFromDto(items), page, pageSize, totalItems, totalPages };
  }
}
